import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { Button, Stack } from 'react-bootstrap';
import { BsLink } from 'react-icons/bs';
import { Badge } from '../../../components/badge/Badge';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import CurrencyCell from '../../../components/tables/cellComponents/CurrencyCell';
import { DateCell } from '../../../components/tables/cellComponents/DateCell';
import { LinkCell } from '../../../components/tables/cellComponents/LinkCell';
import { ReceiptImage } from '../../../features/dash/views/finance/Receipts/ReceiptImage';
import { useToast } from '../../../context/ToastContext';
import { userCanAdmin_Invoices } from '../../../features/dash/views/finance/Invoices/InvoicesView';
import { userCanViewEdit_Payments } from '../../../features/dash/views/finance/Payments/PaymentsView';
import { useDeleteExpenseMutation } from '../../../services/endpoints/finance/expense';
import { getCurrentUser } from '../../../services/helper';
import { Course } from '../../../types/course.type';
import { Expense, Invoice, InvoiceItem, Payment, Receipt } from '../../../types/finance.types';
import { dateToUTC, formatDate } from '../../dateUtils';
import { getErrorMessage, getPartition } from '../../utils';
import {
  expensesCategories,
  invoiceStatusColors,
  paymentMethods,
  receiptStatuses
} from '../finance/finance';

const DeleteExpenseButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteExpense, { isLoading }] = useDeleteExpenseMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteExpense(id).unwrap();
      addToast('Expense deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Remove
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Expense"
        message="Are you sure you want to delete this expense?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
      />
    </>
  );
};

const currentUser = getCurrentUser();

export const invoicesColumns: ColumnDef<Invoice>[] = [
  {
    header: 'ID',
    accessorKey: 'id'
  },
  {
    header: 'Location',
    accessorKey: 'location_id',
    cell: ({ row }) => row.original.location?.title ?? 'N/A'
  },
  {
    header: 'Invoice Name',
    accessorKey: 'name'
  },
  {
    header: 'Sessions',
    enableSorting: false,
    cell: ({ row }) => (
      <div>
        {row.original.courses.map((course, idx) => (
          <LinkCell
            key={idx}
            content={course.title}
            url={`/admin/schedule/sessions/${course.id}`}
          />
        ))}
      </div>
    )
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => (
      <Badge
        title={row.original.status}
        color={invoiceStatusColors[row.original.status]}
      />
    )
  },
  {
    header: 'Issue On',
    accessorKey: 'issue_on',
    cell: ({ row }) =>
      row.original.issue_on ? dateToUTC(new Date(row.original.issue_on), 'MM/dd/yyyy') : 'N/A'
  },
  {
    header: 'Due On',
    accessorKey: 'due_on',
    cell: ({ row }) => {
      return row.original.due_on
        ? dateToUTC(new Date(row.original.due_on), 'MMMM dd, yyyy')
        : 'N/A';
    }
  },
  {
    header: 'Paid At',
    accessorKey: 'paid_at',
    cell: ({ row }) => {
      return row.original.paid_at
        ? dateToUTC(new Date(row.original.paid_at), 'MMMM dd, yyyy h:mm')
        : 'N/A';
    }
  },
  {
    header: 'Total',
    accessorKey: 'total',
    cell: ({ row }) => <CurrencyCell value={row.original.total} />
  },
  // {
  //   header: 'Amount Due',
  //   accessorKey: 'remaining_balance',
  //   cell: ({ row }) => <CurrencyCell value={row.original.remaining_balance} />,
  // },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/finance/invoices/${row.original.id}`}
          underline
        />
        <LinkCell
          content="Edit"
          url={`/admin/finance/invoices/${row.original.id}/edit`}
          underline
        />
      </Stack>
    )
  }
];

export const invoicesClientColumns: ColumnDef<Invoice>[] = [
  {
    header: 'ID',
    accessorKey: 'id'
  },
  {
    header: 'Location',
    accessorKey: 'location_id',
    cell: ({ row }) => row.original.location?.title ?? 'N/A'
  },
  {
    header: 'Invoice Name',
    accessorKey: 'name'
  },
  {
    header: 'Sessions',
    enableSorting: false,
    cell: ({ row }) => (
      <div>
        {row.original.courses.map((course, idx) => (
          <LinkCell
            key={idx}
            content={course.title}
            url={`/admin/schedule/sessions/${course.id}`}
          />
        ))}
      </div>
    )
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => (
      <Badge
        title={row.original.status}
        color={invoiceStatusColors[row.original.status]}
      />
    )
  },
  {
    header: 'Issue On',
    accessorKey: 'issue_on',
    cell: ({ row }) => dateToUTC(new Date(row.original.issue_on), 'MM/dd/yyyy')
  },
  {
    header: 'Due On',
    accessorKey: 'due_on',
    cell: ({ row }) => dateToUTC(new Date(row.original.due_on), 'MMMM dd, yyyy')
  },
  {
    header: 'Total',
    accessorKey: 'total',
    cell: ({ row }) => <CurrencyCell value={row.original.total} />
  },
  {
    header: 'Amount Due',
    accessorKey: 'remaining_balance',
    cell: ({ row }) => <CurrencyCell value={row.original.remaining_balance} />
  },
  {
    header: () => null,
    enableSorting: false,
    accessorKey: 'status',
    cell: ({ row }) => <Button disabled={Boolean(row.original.status)}>Pay</Button>
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/finance/invoices/${row.original.id}`}
          underline
        />
        <LinkCell
          content="Edit"
          url={`/admin/finance/invoices/${row.original.id}/edit`}
          underline
        />
      </Stack>
    )
  }
];

export const invoiceSessionsColumns: ColumnDef<Course>[] = [
  {
    header: 'Session ID',
    id: 'session_id',
    accessorKey: 'id'
  },
  {
    header: 'Session Name',
    id: 'session_name',
    accessorKey: 'title',
    cell: ({ row }) => (
      <LinkCell
        content={row.original.title}
        url={`/admin/schedule/sessions/${row.original.id}`}
      />
    )
  },
  {
    header: 'Amount Receivable',
    accessorKey: 'amount_receivable',
    cell: ({ getValue }) => <CurrencyCell value={getValue<number>()} />
  },
  {
    header: 'Amount Invoiced',
    accessorKey: 'amount_invoiced',
    cell: ({ getValue }) => <CurrencyCell value={getValue<number>()} />
  },
  {
    header: 'Invoicer',
    id: 'invoicer', // TODO: add invoicer tool link
    cell: ({ row }) => (
      <LinkCell
        content={<BsLink size={20} />}
        url={`/admin/schedule/sessions/invoicer/${row.original.id}`}
      />
    )
  }
];

export const invoiceItemsColumns: ColumnDef<InvoiceItem>[] = [
  {
    header: 'ID',
    id: 'invoice_item_id',
    accessorKey: 'id'
  },
  {
    header: 'Description',
    accessorKey: 'description'
  },
  {
    header: 'Enrollment',
    accessorKey: 'enrollment_id'
    // TODO: add enrollment cell link here
  },
  {
    header: 'Unit Price',
    accessorKey: 'unit_price',
    cell: ({ getValue }) => <CurrencyCell value={getValue<number>()} />
  },
  {
    header: 'Quantity',
    accessorKey: 'quantity'
  },
  {
    header: 'Total',
    id: 'total',
    cell: ({ row }) => (
      <CurrencyCell value={(row.original.quantity ?? 0) * row.original.unit_price} />
    )
  },
  {
    header: () => null,
    id: 'invoice_item_actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/finance/invoice-items/${row.original.id}`}
          underline
        />

        {userCanAdmin_Invoices(currentUser) && (
          <>
            <LinkCell
              content="Edit"
              url={`/admin/finance/invoice-items/${row.original.id}/edit`}
              underline
            />
            {/* <LinkCell content="Delete" url={`/admin/finance/invoice-items/${row.original.id}/delete`} underline /> */}
          </>
        )}
      </Stack>
    )
  }
];

export const paidExpensesColumns: ColumnDef<Expense>[] = [
  {
    header: 'ID',
    accessorKey: 'id'
  },
  {
    header: 'Invoice Number',
    accessorKey: 'invoice_number'
  },
  {
    header: 'Due On',
    accessorKey: 'due_on',
    cell: ({ getValue }) => {
      const value = getValue<string>();
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return <></>;
      }
      return date ? dateToUTC(date, 'MMMM d, yyyy') : '';
    }
  },
  {
    header: 'Paid On',
    accessorKey: 'paid_on',
    cell: ({ getValue }) => {
      const value = getValue<string>();
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return <></>;
      }
      return date ? dateToUTC(date, 'MMMM d, yyyy') : '';
    }
  },
  {
    header: `Total Amount Due`,
    accessorKey: 'total',
    enableSorting: false,
    cell: ({ getValue }) => <CurrencyCell value={getValue<number>()} />
  },
  {
    header: 'Payment Method',
    accessorKey: 'payment_method',
    // cell: ({ getValue }) => paymentMethods[getValue<string>()]
    cell: ({ row, getValue }) => {
      return paymentMethods[getValue<string>()] === 'Check' && row.original.check_number
        ? paymentMethods[getValue<string>()] + ' #' + row.original.check_number
        : paymentMethods[getValue<string>()];
    }
  },
  {
    header: 'Session',
    accessorKey: 'course_id',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.course ? (
        <LinkCell
          content={row.original.course.title}
          url={`/admin/schedule/sessions/${row.original.course_id}`}
        />
      ) : (
        'N/A'
      )
  },
  {
    header: 'Expense Tool Notes',
    accessorKey: 'notes',
    enableSorting: false
  },
  {
    header: 'Facility Use Fee Payment Mailing Details',
    enableSorting: false,
    cell: ({ row }) => row.original.course?.venue?.facility_use_fee_payment_details ?? 'N/A'
  },
  {
    header: 'Category',
    accessorKey: 'category',
    cell: ({ getValue }) => expensesCategories[getValue<string>()]
  },
  {
    header: 'Admin',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.admin ? (
        <LinkCell
          content={row.original.admin.full_name}
          url=""
        />
      ) : (
        'N/A'
      )
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/finance/expenses/${row.original.id}`}
          underline
        />
        <LinkCell
          content="Edit"
          url={`/admin/finance/expenses/${row.original.id}/edit`}
          underline
        />
      </Stack>
    )
  }
];

export const expensesColumns: ColumnDef<Expense>[] = [
  {
    header: 'ID',
    accessorKey: 'id'
  },
  {
    header: 'Invoice Number',
    accessorKey: 'invoice_number'
  },
  {
    header: 'Due On',
    accessorKey: 'due_on',
    cell: ({ getValue }) => {
      const value = getValue<string>();
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return <></>;
      }
      return date ? dateToUTC(date, 'MMMM d, yyyy') : '';
    }
  },
  {
    header: `Total Amount Due`,
    accessorKey: 'total',
    enableSorting: false,
    cell: ({ getValue }) => <CurrencyCell value={getValue<number>()} />
  },
  {
    header: 'Payment Method',
    accessorKey: 'payment_method',
    cell: ({ row, getValue }) => {
      return paymentMethods[getValue<string>()] === 'Check' && row.original.check_number
        ? paymentMethods[getValue<string>()] + ' #' + row.original.check_number
        : paymentMethods[getValue<string>()];
    }
  },
  {
    header: 'Session',
    accessorKey: 'course_id',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.course ? (
        <LinkCell
          content={row.original.course.title}
          url={`/admin/schedule/sessions/${row.original.course_id}`}
        />
      ) : (
        'N/A'
      )
  },
  {
    header: 'Expense Tool Notes',
    accessorKey: 'notes',
    enableSorting: false
  },
  {
    header: 'Facility Use Fee Payment Mailing Details',
    enableSorting: false,
    cell: ({ row }) => row.original.course?.venue?.facility_use_fee_payment_details ?? 'N/A'
  },
  {
    header: 'Category',
    accessorKey: 'category',
    cell: ({ getValue }) => expensesCategories[getValue<string>()]
  },
  {
    header: 'Admin',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.admin ? (
        <LinkCell
          content={row.original.admin.full_name}
          url=""
        />
      ) : (
        'N/A'
      )
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/finance/expenses/${row.original.id}`}
          underline
        />
        <LinkCell
          content="Edit"
          url={`/admin/finance/expenses/${row.original.id}/edit`}
          underline
        />
        <DeleteExpenseButton id={row.original.id} />
        {/* TODO: add unarchive link */}
      </Stack>
    )
  }
];

export const receiptsColumns: ColumnDef<Receipt>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/finance/receipts/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Image',
    accessorKey: 'image_file_name',
    enableSorting: false,
    cell: ({ row }) => {
      if (!row.original.signed_url) {
        return 'N/A';
      }
      return (
          <LinkCell
              content={
                <ReceiptImage
                    url={row.original.signed_url}
                    fileName={row.original.image_file_name}
                    alt="Receipt"
                    style={{ width: 48, height: 48, maxHeight: 48, objectFit: 'cover', borderRadius: 4 }}
                />
              }
              url={`/admin/finance/receipts/${row.original.id}`}
          />
      );
    }
  },
  {
    header: 'User',
    accessorKey: 'user_id',
    cell: ({ row }) =>
      row.original.user ? (
        <LinkCell
          content={row.original.user.full_name}
          url={`/admin/dash-users/instructors/${row.original.user.id}`}
        />
      ) : (
        'N/A'
      )
  },
  {
    header: 'Session',
    accessorKey: 'course_id',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.course ? (
        <LinkCell
          content={row.original.course.title}
          url={`/admin/schedule/sessions/${row.original.course.id}`}
        />
      ) : (
        'N/A'
      )
  },
  {
    header: 'Store Name',
    accessorKey: 'store_name'
  },
  {
    header: 'Date of Purchase',
    accessorKey: 'purchased_at',
    cell: ({ getValue }) => (
      <DateCell
        date={getValue<string>()}
        format={'MM/dd/yyyy'}
      />
    )
  },
  {
    header: 'Total',
    accessorKey: 'total',
    cell: ({ getValue }) => <CurrencyCell value={getValue<number>()} />
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ getValue }) => {
      let index = getValue<number>();
      let status = index in receiptStatuses ? receiptStatuses[index] : 'unknown';
      return status;
    }
  }
];

export const paymentsColumns: ColumnDef<Payment>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/finance/payments/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Invoice Name',
    accessorKey: 'invoice_id',
    id: 'invoice_name',
    cell: ({ row }) =>
      row.original.invoice ? (
        <LinkCell
          content={row.original.invoice.name}
          url={`/admin/finance/invoices/${row.original.invoice.id}`}
        />
      ) : (
        'N/A'
      )
  },
  {
    header: 'Invoice Client',
    accessorKey: 'invoice_id',
    cell: ({ row }) =>
      row.original.invoice?.invoice_client ? (
        <LinkCell
          content={row.original.invoice.invoice_client.name}
          url={`/admin/invoice-clients/${row.original.invoice.invoice_client.id}`}
        />
      ) : (
        'N/A'
      )
  },
  {
    header: 'Location',
    accessorKey: 'invoice_id',
    id: 'invoice_location',
    cell: ({ row }) =>
      row.original.invoice?.location ? (
        <LinkCell
          content={row.original.invoice.location.title}
          url={`/admin/places/locations/${row.original.invoice.location.id}`}
        />
      ) : (
        'N/A'
      )
  },
  {
    header: 'Payment Method',
    accessorKey: 'payment_method',
    cell: ({ getValue }) => paymentMethods[getValue<string>()]
  },
  {
    header: 'Check Number',
    accessorKey: 'check_number'
  },
  {
    header: `Total`,
    accessorKey: 'total',
    cell: ({ getValue }) => <CurrencyCell value={getValue<number>()} />
  },
  {
    header: 'Paid At',
    accessorKey: 'created_at',
    cell: ({ getValue }) => {
      return formatDate(getValue<Date>(), 'MM/dd/yyyy hh:mm a');
    }
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        {userCanViewEdit_Payments(currentUser) && (
          <>
            <LinkCell
              content="View"
              url={`/admin/finance/payments/${row.original.id}`}
              underline
            />
            <LinkCell
              content="Edit"
              url={`/admin/finance/payments/${row.original.id}/edit`}
              underline
            />
          </>
        )}
      </Stack>
    )
  }
];
